import { inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, EMPTY, Observable, Subject, tap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import ProjectLinks from 'src/app/common/project.links';
import { PaginationState } from 'src/app/interfaces/pagination.interface';
import { RemovePostContent } from 'src/app/interfaces/posts.interface';
import { SearchState } from 'src/app/interfaces/search.interface';
import { PostBasic, Posts, DefaultService as RestApiService, UserSocialGroupsStats } from 'src/app/swagger-api';
import { SortCategories } from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import { SearchCategories, SearchStrategyTypes } from '../search/search.enum';
import { SearchService } from '../search/search.service';
import { sortParams } from '../shared/list-controller/list-controller';
import { SocialInitiativePostsEntities } from '../shared/social-initiative/social-initiative-posts-entities.abstract';
import { defineCallParams } from '../shared/social-initiative/social-initiative.helpers';

@Injectable({ providedIn: 'root' })
export class PostsService extends SocialInitiativePostsEntities implements OnDestroy {
  private socialUserGroups$: Subject<UserSocialGroupsStats> = new Subject();

  private searchPost$: BehaviorSubject<Posts> = new BehaviorSubject({ items: [], total_count: 0 });
  private postsSubject$: BehaviorSubject<Posts> = new BehaviorSubject({ items: [], total_count: 0 });

  private router = inject(Router);
  private apiService = inject(RestApiService);
  private searchService = inject(SearchService);
  private sessionService = inject(SessionService);

  public constructor() {
    super();

    this.searchService.searchResultsChanges
      .pipe(
        takeUntilDestroyed(),
        tap((result) => {
          this.searchPost$.next({
            total_count: result.total_matches_count,
            items: result.search_results.map(({ item }) => item as PostBasic),
          });
        }),
      )
      .subscribe();
  }

  public override ngOnDestroy(): void {
    if (this.postsSubject$) {
      this.postsSubject$.complete();
    }
  }

  public get postsChanges(): Observable<Posts> {
    return this.postsSubject$.asObservable();
  }

  public get searchPostsChanges(): Observable<Posts> {
    return this.searchPost$.asObservable();
  }

  public get socialUserGroupsChanges(): Observable<UserSocialGroupsStats> {
    return this.socialUserGroups$.asObservable();
  }

  /**
   * @description Fetches all posts of current user.
   * @param { SearchState } state
   */
  public fetchCurrentUserPosts(state?: SearchState): void {
    const params: [offset: number, limit: number] = defineCallParams(null, state);
    this.apiService
      .restSocialProfilePostsIndex(this.sessionService.userId, ...params, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((posts: Posts) => this.notifyAboutPostsChanges(posts));
  }

  /**
   * @description Fetches project's all posts by its id.
   * @param { Project } project
   * @param { SearchState } state
   */
  public fetchProjectPosts(projectId: string, state?: SearchState): void {
    const params: [offset: number, limit: number] = defineCallParams(null, state);
    this.apiService
      .restSocialProjectPostsIndex(projectId, ...params, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((posts: Posts) => this.notifyAboutPostsChanges(posts));
  }

  /**
   * @description Fetches community's all posts by its id.
   * @param { Community } community
   * @param { SearchState } state
   */
  public fetchCommunityPosts(communityId: string, state?: SearchState): void {
    const [offset, limit]: number[] = defineCallParams(null, state);
    this.apiService.restSocialCommunityPostsIndex(communityId, offset, limit, loadingHttpContext()).subscribe({
      next: (posts: Posts) => this.notifyAboutPostsChanges(posts),
      error: () => {
        this.notifyAboutPostsChanges({
          items: null,
          total_count: 0,
        });
      },
    });
  }

  public searchPosts(keyword: string, pagination: PaginationState, sorting: SortCategories): void {
    if (!keyword) {
      return;
    }

    const state: SearchState = {
      pagination,
      sortParams: sortParams[sorting],
      params: { queryText: keyword, itemKind: SearchCategories.POSTS },
      excludeIds: [this.sessionService.userId],
    };

    this.searchService.setStrategy(SearchStrategyTypes.DEFAULT);
    this.searchService.getSearchResults({ state, applyLocationAttrs: true });

    //
  }

  /**
   * @description Removes post with specified postId if response value isn't empty.
   *              Emits the updated posts list on successful deleting.
   * @param { RemovePostContent } content
   */
  public removePost(content: RemovePostContent): void {
    this.apiService
      .restSocialPostsDelete(content.postId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_POSTS, { replaceUrl: true }));
        this.afterRemovingPostHandler(content);
      });
  }

  public fetchRequestSocialGroups(): void {
    this.apiService
      .restSocialUserGroupsStatsGet()
      .pipe(catchError(() => EMPTY))
      .subscribe((groups: UserSocialGroupsStats) => {
        this.socialUserGroups$.next(groups);
      });
  }

  protected fetchUserSpecificEntities(userId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialProfilePostsIndex(userId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((posts: Posts) => this.postsSubject$.next(posts));
  }

  protected fetchEntityRelatedItemsResolver(entityId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialProfilePostsIndex(entityId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((posts: Posts) => this.postsSubject$.next(posts));
  }

  /**
   * @description Refetches posts after it's removing depend on incoming content.
   * @param { RemovePostContent } content
   */
  private afterRemovingPostHandler(content: RemovePostContent): void {
    if (!content.info) {
      this.fetchCurrentUserPosts(content.state);
      return;
    }

    if ('validity_period' in content.info) {
      this.fetchProjectPosts(content.info.entity_id, content.state);
      return;
    }

    this.fetchCommunityPosts(content.info.entity_id, content.state);
  }

  private notifyAboutPostsChanges(posts: Posts): void {
    this.postsSubject$.next(posts);
  }
}
